import React from 'react'
import { SizeVariants } from '@/shared/forms/components/constants'
import clsx from 'clsx'

export type DurationInputProps = {
  label: string
  size: keyof typeof SizeVariants
  touched?: boolean
  error?: string
  disabled?: boolean
  optional?: boolean

  yearInputProps: Omit<
    FieldInputProps,
    'label' | 'disabled' | 'classNames' | 'hasError'
  >
  monthInputProps: Omit<
    FieldInputProps,
    'label' | 'disabled' | 'classNames' | 'hasError'
  >
}

export default function DurationInput({
  label,
  size,
  disabled = false,
  touched = false,
  error = '',
  optional = false,

  yearInputProps,
  monthInputProps,
}: DurationInputProps) {
  return (
    <div className={SizeVariants[size as keyof typeof SizeVariants]}>
      <fieldset>
        <div className="flex items-end justify-between">
          <legend className="block text-sm font-medium leading-6 text-gray-900">
            {label}
          </legend>
          {optional && (
            <span
              className="text-sm leading-6 text-gray-500"
              id="email-optional"
            >
              Optional
            </span>
          )}
        </div>

        <div className="mt-2 -space-y-px rounded-md bg-white shadow-sm">
          <div className="flex -space-x-px">
            <FieldInput
              {...yearInputProps}
              disabled={disabled}
              label="Years"
              classNames="rounded-l-md"
              hasError={touched && error?.length > 0}
            />
            <FieldInput
              {...monthInputProps}
              disabled={disabled}
              label="Months"
              classNames="rounded-r-md"
              hasError={touched && error?.length > 0}
            />
          </div>
        </div>
      </fieldset>
      {touched && error && (
        <div className="mt-2 text-sm font-semibold text-red-600">{error}</div>
      )}
    </div>
  )
}

type FieldInputProps = {
  id?: string
  label: string
  name: string
  onChange?: (e: React.ChangeEvent<any>) => void
  onBlur?: (e: React.FocusEvent<any>) => void
  value?: number
  disabled?: boolean
  classNames?: string
  hasError: boolean
}

function FieldInput({
  id,
  label,
  name,
  onChange,
  onBlur,
  value,
  hasError,
  classNames,
}: FieldInputProps) {
  return (
    <div className="relative w-1/2 min-w-0 flex-1">
      <label htmlFor="card-expiration-date" className="sr-only">
        {label}
      </label>
      <input
        type="text"
        name={name}
        id={id}
        className={clsx(
          'relative block w-full rounded-none border-0 bg-transparent py-1.5 text-gray-900 ring-1 ring-inset placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6',
          classNames,
          hasError ? 'ring-red-600' : 'ring-gray-300',
        )}
        onChange={onChange}
        onBlur={onBlur}
        value={value}
        inputMode="numeric"
        placeholder="0"
      />
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
        <span className="text-gray-500 sm:text-sm" id="price-currency">
          {label}
        </span>
      </div>
    </div>
  )
}
